import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import { Toaster } from "sonner";

import { AuthProvider } from "@/contexts/AuthContext";
import { SettingsProvider } from "@/contexts/SettingsContext";

import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import SiteTracking from "@/components/site/SiteTracking";

import HomePage from "@/pages/HomePage";
import BikesPage from "@/pages/BikesPage";
import BikeDetailPage from "@/pages/BikeDetailPage";
import ComparePage from "@/pages/ComparePage";
import EmiPage from "@/pages/EmiPage";
import DealersPage from "@/pages/DealersPage";
import NewsPage from "@/pages/NewsPage";
import NewsDetailPage from "@/pages/NewsDetailPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import ContactPage from "@/pages/ContactPage";
import AboutUsPage from "@/pages/AboutUsPage";
import AdminPanel from "@/pages/AdminPanel";
import NotFoundPage from "@/pages/NotFoundPage";

// Detect if we're on the admin subdomain (admin.servallbikes.in)
// Returns true for admin.* hosts so we can serve admin-only routes there.
const isAdminHost = () => {
    if (typeof window === "undefined") return false;
    return /^admin\./i.test(window.location.hostname);
};

function Shell({ children, hideChrome }) {
    if (hideChrome) return <>{children}</>;
    return (
        <>
            <Navbar />
            <main className="min-h-[60vh] overflow-x-hidden">{children}</main>
            <Footer />
        </>
    );
}

// Reset window scroll on route change; honour hash targets (e.g. /#inventory).
const ScrollToTop = () => {
    const { pathname, hash } = useLocation();
    useEffect(() => {
        if (hash) {
            const id = hash.slice(1);
            let tries = 0;
            const go = () => {
                const el = document.getElementById(id);
                if (el) {
                    el.scrollIntoView({ behavior: "smooth", block: "start" });
                    return;
                }
                if (tries++ < 20) requestAnimationFrame(go);
            };
            // Defer so the destination page can mount first
            requestAnimationFrame(go);
            return;
        }
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }, [pathname, hash]);
    return null;
};

// Adds <meta name="robots" content="noindex,nofollow"> when route is admin/auth.
// Removes the tag on public routes so SEO crawling works.
const RobotsControl = () => {
    const { pathname } = useLocation();
    useEffect(() => {
        const isAdminRoute = isAdminHost() || pathname.startsWith("/auth/secure-gate") || pathname.startsWith("/admin");
        let tag = document.querySelector('meta[name="robots"][data-dynamic="1"]');
        if (isAdminRoute) {
            if (!tag) {
                tag = document.createElement("meta");
                tag.setAttribute("name", "robots");
                tag.setAttribute("data-dynamic", "1");
                document.head.appendChild(tag);
            }
            tag.setAttribute("content", "noindex,nofollow");
        } else if (tag) {
            tag.remove();
        }
    }, [pathname]);
    return null;
};

// On the admin subdomain, all public paths redirect to /auth/secure-gate.
const AdminSubdomainRoutes = () => (
    <Routes>
        <Route path="/auth/secure-gate/*" element={<Shell hideChrome><AdminPanel /></Shell>} />
        <Route path="*" element={<Navigate to="/auth/secure-gate" replace />} />
    </Routes>
);

const PublicRoutes = () => (
    <Routes>
        <Route path="/" element={<Shell><HomePage /></Shell>} />
        <Route path="/bikes" element={<Shell><BikesPage /></Shell>} />
        <Route path="/bikes/:id" element={<Shell><BikeDetailPage /></Shell>} />
        <Route path="/compare" element={<Shell><ComparePage /></Shell>} />
        <Route path="/emi" element={<Shell><EmiPage /></Shell>} />
        <Route path="/dealers" element={<Shell><DealersPage /></Shell>} />
        <Route path="/news" element={<Shell><NewsPage /></Shell>} />
        <Route path="/news/:slug" element={<Shell><NewsDetailPage /></Shell>} />
        <Route path="/contact" element={<Shell><ContactPage /></Shell>} />
        <Route path="/about" element={<Shell><AboutUsPage /></Shell>} />
        <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
        <Route path="/privacy-policy" element={<Shell><PrivacyPolicyPage /></Shell>} />
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="/signup" element={<Navigate to="/" replace />} />
        <Route path="/profile" element={<Navigate to="/" replace />} />
        <Route path="/admin/*" element={<Navigate to="/auth/secure-gate" replace />} />
        <Route path="/auth/secure-gate/*" element={<Shell hideChrome><AdminPanel /></Shell>} />
        <Route path="*" element={<Shell><NotFoundPage /></Shell>} />
    </Routes>
);

function App() {
    const adminMode = isAdminHost();
    return (
        <div className="App">
            <SettingsProvider>
                <AuthProvider>
                    <BrowserRouter>
                        <ScrollToTop />
                        <RobotsControl />
                        <SiteTracking />
                        {adminMode ? <AdminSubdomainRoutes /> : <PublicRoutes />}
                    </BrowserRouter>
                    <Toaster richColors position="top-right" />
                </AuthProvider>
            </SettingsProvider>
        </div>
    );
}

export default App;
